import {isEmpty, isNil} from 'ramda';
import {isArray, isPlainObj, isString} from 'ramda-adjunct';

type RowCells = Record<string, unknown>;

/**
 * Groups the selected tires by the set they belong to, keyed by set id.
 *
 * Bulk actions receive `getColumnData` stubbed to `null` (see `executeBulkAction`), so the set a
 * tire belongs to can only be read per row off `rowData`. Returns `null` when the selection is
 * empty or any row's `orderSetId` is unreadable: `setId` travels in the request path, so a
 * partial grouping would let the caller post wheels under a set they do not belong to.
 *
 * The cell read duplicates `getRowCellValue` from `features/eag-grid`, which cannot be reused
 * here: it takes `RowData`, while `ActionCallbackParams.rowData` is typed
 * `Record<string, unknown>` even though both dispatch paths pass real rows.
 */
export function groupWheelsBySet(
  rowData: RowCells | RowCells[]
): Record<string, {id: string}[]> | null {
  const rows = isArray(rowData) ? rowData : [rowData];

  if (isEmpty(rows)) {
    return null;
  }

  const wheelsBySet: Record<string, {id: string}[]> = {};

  for (const row of rows) {
    const cell = row.orderSetId;
    // An empty set id has to fail here too — it would collapse the request path to `/tire-set//`.
    const setId =
      isPlainObj(cell) && 'value' in cell && isString(cell.value) && !isEmpty(cell.value)
        ? cell.value
        : null;
    const rowId = row.id;

    // `!isString` rather than ramda-adjunct's `isNotString`, which returns a plain boolean and so
    // would leave `rowId` as `unknown`.
    if (isNil(setId) || !isString(rowId)) {
      return null;
    }

    (wheelsBySet[setId] ??= []).push({id: rowId});
  }

  return wheelsBySet;
}
